import React from 'react';
import gql from 'graphql-tag';
import { useApolloClient, useMutation } from '@apollo/client';
import { DateTime } from 'luxon';
import EventsSynchronizer from '~Components/EventsSynchronizer';

import { useDates, useLogger } from '~Hooks';
import { useStorage } from '~Components/Storage';
import {
  AddDeviceSettingsEvents,
  AddDeviceSettingsEventsVariables,
  GetDeviceSettingsEvents,
  GetDeviceSettingsEventsVariables,
  GetDeviceSettingsSnapshot,
  GetDeviceSettingsSnapshotVariables,
} from './__queries__';

const SETTINGS_EVENTS_SNAPSHOTS_QUERY = gql`
  query GetDeviceSettingsSnapshot($deviceId: String!, $date: DateTime!) {
    Device(id: $deviceId) {
      id
      settingsSnapshot(date: $date) {
        snapshot
      }
    }
  }
`;

const SETTINGS_EVENTS_QUERY = gql`
  query GetDeviceSettingsEvents($deviceId: String!, $startDate: DateTime!, $endDate: DateTime!) {
    Device(id: $deviceId) {
      id
      settingsEvents(startDate: $startDate, endDate: $endDate) {
        items
      }
    }
  }
`;

const ADD_SETTINGS_MUTATION = gql`
  mutation AddDeviceSettingsEvents($deviceId: String!, $events: Json!) {
    putDeviceEvents(deviceId: $deviceId, events: $events) {
      success
      message
    }
  }
`;

interface Props {
  venueId: string;
}

const DeviceSettingsSynchronizer: React.FunctionComponent<Props> = ({ venueId }) => {
  const client = useApolloClient();
  const dates = useDates();
  const [{ deviceId }] = useStorage();
  const logger = useLogger('device-settings-synchronizer');

  const [addEvents] = useMutation<AddDeviceSettingsEvents, AddDeviceSettingsEventsVariables>(
    ADD_SETTINGS_MUTATION,
  );

  if (!deviceId) {
    return null;
  }

  return (
    <EventsSynchronizer
      venueId={venueId}
      eventType="ADD_DEVICE_SETTINGS_EVENT"
      manyEventsType="ADD_DEVICE_SETTINGS_EVENTS"
      snapshotType="SET_DEVICE_SETTINGS_SNAPSHOT"
      pollingInterval={5000}
      eventSelector={(state) => state.deviceSettingsEvents}
      snapshotSelector={(state) => state.deviceSettingsSnapshot}
      /* Polling query for new events */
      queryEvents={async (timestamp) => {
        const endDate = new Date();
        let startDate = new Date(timestamp);

        /* Ideally this should never happen, but if there has been no setting events this day and the clock goes to 00:00, we then default to 04:00 that same day in <EventsSynchronizer /> */
        if (startDate > endDate) {
          logger.error('startDate is ahead of endDate - time sync issue detected', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            rawTimestamp: timestamp,
          });
          const previousDay = DateTime.fromJSDate(endDate)
            .minus({ days: 1 })
            .toJSDate();
          startDate = new Date(dates.getAccountingStartDate(previousDay));
        }

        const response = await client.query<
          GetDeviceSettingsEvents,
          GetDeviceSettingsEventsVariables
        >({
          query: SETTINGS_EVENTS_QUERY,
          variables: {
            deviceId,
            startDate,
            endDate,
          },
          fetchPolicy: 'network-only',
        });

        const success = Boolean(response.data?.Device?.settingsEvents?.items);

        return {
          success,
          events: response.data?.Device?.settingsEvents?.items || [],
          error: response.error?.message || null,
        };
      }}
      /* Snapshot query */
      querySnapshot={async () => {
        const response = await client.query<
          GetDeviceSettingsSnapshot,
          GetDeviceSettingsSnapshotVariables
        >({
          query: SETTINGS_EVENTS_SNAPSHOTS_QUERY,
          variables: {
            deviceId,
            date: new Date(),
          },
          fetchPolicy: 'network-only',
        });

        const snapshot = response.data?.Device?.settingsSnapshot?.snapshot;

        return {
          success: Boolean(response.data?.Device.settingsSnapshot.snapshot),
          snapshot: snapshot || null,
          timestamp: snapshot?.date || null,
          error: response.error?.message || null,
        };
      }}
      addEventMutation={async (events) => {
        try {
          const response = await addEvents({
            variables: {
              deviceId,
              events,
            },
          });

          if (!response.data?.putDeviceEvents.success) {
            return {
              success: false,
              error: response.errors?.map((error) => error.message).join(', ') || 'Unknwown',
            };
          }

          return {
            error: null,
            success: true,
          };
        } catch (e) {
          return {
            success: false,
            // @ts-ignore
            error: e.message,
          };
        }
      }}
    />
  );
};

export default DeviceSettingsSynchronizer;
